<script setup lang="ts">
import { ref, computed } from 'vue'
import { MessageSquare, Users, User, Search, X } from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'

interface Conversation {
  id: number
  type: string
  title: string
  last_message: {
    body: string
    sender_name: string
    sent_at: string
  } | null
  unread_count: number
  participant_count: number
  teacher: {
    id: number
    name: string
    avatar_url: string | null
  }
  subject: { id: number; name: string; color: string | null } | null
  level: { id: number; name: string } | null
}

const props = withDefaults(defineProps<{
  conversations: Conversation[]
  activeConversationId: number | null
  emptyMessage?: string
}>(), {
  emptyMessage: 'Messages will appear here when you have an active subscription',
})

const emit = defineEmits<{
  select: [conversationId: number]
}>()

const searchQuery = ref('')

const filteredConversations = computed(() => {
  if (!searchQuery.value.trim()) return props.conversations
  const query = searchQuery.value.toLowerCase()
  return props.conversations.filter(c =>
    c.title.toLowerCase().includes(query) ||
    c.teacher.name.toLowerCase().includes(query) ||
    c.subject?.name.toLowerCase().includes(query) ||
    c.level?.name.toLowerCase().includes(query)
  )
})

</script>

<template>
  <div class="flex flex-col h-full">
    <div class="px-4 py-3 border-b border-gray-200">
      <h2 class="text-lg font-semibold text-gray-900">Messages</h2>
      <div v-if="conversations.length > 0" class="relative mt-2">
        <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search conversations..."
          class="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          v-if="searchQuery"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          @click="searchQuery = ''"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>

    <div v-if="conversations.length === 0" class="flex-1 flex items-center justify-center p-6">
      <div class="text-center">
        <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <MessageSquare class="w-6 h-6 text-gray-400" />
        </div>
        <p class="text-sm text-gray-500">No conversations yet</p>
        <p class="text-xs text-gray-400 mt-1">{{ emptyMessage }}</p>
      </div>
    </div>

    <div v-else-if="filteredConversations.length === 0" class="flex-1 flex items-center justify-center p-6">
      <div class="text-center">
        <p class="text-sm text-gray-500">No conversations match "{{ searchQuery }}"</p>
      </div>
    </div>

    <div v-else class="flex-1 overflow-y-auto scrollbar-hover">
      <button
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        class="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100"
        :class="{ 'bg-blue-50 hover:bg-blue-50': activeConversationId === conversation.id }"
        @click="emit('select', conversation.id)"
      >
        <!-- Avatar -->
        <div class="relative shrink-0">
          <Avatar class="w-10 h-10" v-if="conversation.type === 'direct'">
            <AvatarImage :src="conversation.teacher.avatar_url" />
            <AvatarFallback />
          </Avatar>
          <div
            v-else
            class="w-10 h-10 rounded-full flex items-center justify-center bg-brand-soft"
          >
            <Users class="w-5 h-5 text-brand" />
          </div>
        </div>

        <!-- Content -->
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-medium text-gray-900 truncate">
              {{ conversation.title }}
            </span>
            <span v-if="conversation.last_message" class="text-xs text-gray-400 shrink-0">
              {{ conversation.last_message.sent_at }}
            </span>
          </div>
          <div class="flex items-center justify-between gap-2 mt-0.5">
            <p v-if="conversation.last_message" class="text-xs text-gray-500 truncate">
              <span class="font-medium">{{ conversation.last_message.sender_name }}:</span>
              {{ conversation.last_message.body }}
            </p>
            <p v-else class="text-xs text-gray-400 italic">No messages yet</p>
            <span
              v-if="conversation.unread_count > 0"
              class="shrink-0 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full font-semibold min-w-[20px] text-center"
            >
              {{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}
            </span>
          </div>
          <!-- Type badge -->
          <div class="flex items-center gap-1.5 mt-1">
            <span
              v-if="conversation.type === 'group'"
              class="text-[10px] text-gray-400 flex items-center gap-0.5"
            >
              <Users class="w-3 h-3" />
              {{ conversation.participant_count }} members
            </span>
            <span
              v-else
              class="text-[10px] text-gray-400 flex items-center gap-0.5"
            >
              <User class="w-3 h-3" />
              Direct message
            </span>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>
